import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListedConnector } from "../../../api/connectorsApi";
import AppButton from "../../../ui/buttons/AppButton";
import { MAXIMUM_FALLBACK_ROUTES, movedFallback, sameRoute, withFallback, type TurnRoute } from "./routeChoices";
import FieldGroup from "../../../ui/forms/FieldGroup";
import EmptyState from "../../../ui/feedback/EmptyState";
import LlmPicker, { type LlmChoice } from "../llmChoice/LlmPicker";
import { llmLabel, type ConnectorOptions } from "../llmChoice/llmOptions";

interface FallbackRoutesFieldProps {
  /** The host's PC with its route options, as the one LLM picker offers them (F106). */
  llmChoices: readonly ConnectorOptions[];
  onRefresh: () => void;
  /** The host's PC, whose report names each fallback. */
  connector: ListedConnector;
  /** The Thread's own route, which is never its own fallback. */
  own: TurnRoute | null;
  value: readonly TurnRoute[];
  onChange: (next: readonly TurnRoute[]) => void;
}

/**
 * The Thread's fallback routes, in the order they are tried (Q-93, Q-96): when a turn's
 * own route has a refused key or no credit left, it runs on the first allowed one. Another
 * route may bill another account, so the host chooses each one and its place. Each is chosen
 * in the one LLM picker (BA16-08); this field keeps only their order.
 */
export default function FallbackRoutesField({ llmChoices, onRefresh, connector, own, value, onChange }: FallbackRoutesFieldProps) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  // The LLM chosen in the picker, with any effort it offers, until it is added; only from the
  // choices it was made in: a Refresh that reads them again retires it (Sol R2 S3).
  const [picked, setPicked] = useState<{ readonly choice: LlmChoice; readonly from: readonly ConnectorOptions[] } | null>(null);
  const pending = picked?.from === llmChoices ? picked.choice : null;
  const added = pending === null ? value : withFallback(value, own, pending.route);
  const close = () => { setAdding(false); setPicked(null); };

  return (
    <FieldGroup legend={t("threads.hosting.fallback.title")} className="space-y-2 text-sm">
      <p className="app-help-text text-xs">{t("threads.hosting.fallback.help")}</p>
      {value.length === 0 ? <EmptyState size="small">{t("threads.hosting.fallback.none")}</EmptyState> : null}
      <ol className="space-y-1">
        {value.map((route, index) => (
          <li key={JSON.stringify(route)} className="flex items-center gap-2">
            <span className="app-text-muted w-5 text-right text-xs">{index + 1}.</span>
            <span className="flex-1">{llmLabel(route, connector, t)}</span>
            <AppButton variant="ghost" size="xs" disabled={index === 0} aria-label={t("threads.hosting.fallback.up", { position: index + 1 })} onClick={() => { onChange(movedFallback(value, index, -1)); }}>↑</AppButton>
            <AppButton variant="ghost" size="xs" disabled={index === value.length - 1} aria-label={t("threads.hosting.fallback.down", { position: index + 1 })} onClick={() => { onChange(movedFallback(value, index, 1)); }}>↓</AppButton>
            <AppButton variant="ghost" size="xs" aria-label={t("threads.hosting.fallback.remove", { position: index + 1 })} onClick={() => { onChange(value.filter((_, kept) => kept !== index)); }}>
              {t("threads.hosting.fallback.removeShort")}
            </AppButton>
          </li>
        ))}
      </ol>
      {value.length < MAXIMUM_FALLBACK_ROUTES && llmChoices.length > 0 ? (
        adding ? (
          <LlmPicker
            choices={llmChoices}
            onRefresh={onRefresh}
            value={pending}
            onChoose={(choice) => { setPicked({ choice, from: llmChoices }); }}
            footer={
              <div className="flex gap-2">
                <AppButton variant="secondary" size="xs" disabled={added === value} onClick={() => { onChange(added); close(); }}>{t("threads.hosting.fallback.add")}</AppButton>
                <AppButton variant="ghost" size="xs" onClick={close}>{t("common.cancel")}</AppButton>
              </div>
            }
          />
        ) : (
          <AppButton variant="secondary" size="xs" onClick={() => { setAdding(true); }}>{t("threads.hosting.fallback.chooseRoute")}</AppButton>
        )
      ) : null}
      {pending !== null && sameRoute(own, pending.route) ? <span className="app-help-text text-xs">{t("threads.hosting.fallback.isOwn")}</span> : null}
    </FieldGroup>
  );
}
